import apiClient, { API_BASE_URL } from '../api/apiClient';

const absoluteUrl = (url: string) => (
  url.startsWith('http://') || url.startsWith('https://')
    ? url
    : `${API_BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`
);

const apiOrigin = new URL(API_BASE_URL).origin;

const isApiUrl = (url: string) => {
  try {
    return new URL(url).origin === apiOrigin;
  } catch {
    return false;
  }
};

const withDownloadDisposition = (url: string) => {
  const parsed = new URL(url);
  if (parsed.pathname.startsWith('/api/files/')) parsed.searchParams.set('download', 'true');
  return parsed.toString();
};

const filenameFromDisposition = (value?: string) => {
  if (!value) return undefined;
  const encoded = value.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try { return decodeURIComponent(encoded); } catch { return encoded; }
  }
  return value.match(/filename="?([^";]+)"?/i)?.[1];
};

export const fetchDownloadBlob = async (url: string): Promise<{ blob: Blob; filename?: string }> => {
  const resolvedUrl = withDownloadDisposition(absoluteUrl(url));

  if (isApiUrl(resolvedUrl)) {
    const response = await apiClient.get(resolvedUrl, { responseType: 'blob' });
    return {
      blob: response.data,
      filename: filenameFromDisposition(response.headers?.['content-disposition']),
    };
  }

  const response = await fetch(resolvedUrl);
  if (!response.ok) throw new Error('Unable to download file.');
  return {
    blob: await response.blob(),
    filename: filenameFromDisposition(response.headers.get('content-disposition') || undefined),
  };
};

export const saveBlob = (blob: Blob, filename: string) => {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};

export const downloadFile = async (url: string, fallbackFilename = 'download') => {
  const { blob, filename } = await fetchDownloadBlob(url);
  saveBlob(blob, filename || fallbackFilename);
};

export const downloadExportResponse = async (response: any, fallbackFilename: string) => {
  const payload = response?.data?.data ?? response?.data ?? response;
  const url = payload?.downloadUrl ?? payload?.exportDownloadUrl;
  if (!url || typeof url !== 'string') throw new Error('The export did not provide a download URL.');
  const filename = payload?.fileName ?? payload?.exportFileName ?? fallbackFilename;
  await downloadFile(url, filename);
  return payload;
};
